import CronInput from "@galaxy-io/dls/inputs/CronInput";
import Field from "@galaxy-io/dls/inputs/Field";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import { MenuItem, MenuItemVariant } from "@galaxy-io/dls/menu/Menu";
import Text from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { isSchedulable } from "@gtm/contracts/orchestration";
import { DEFAULT_SCHEDULE_CRON } from "@gtm/contracts/workflows/api";
import { type WorkflowCatalogEntry, workflows } from "@gtm/contracts/workflows/catalog";
import type { WorkflowSchedule } from "@gtm/contracts/workflows/jobs";
import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { RecordTable } from "@/components/workspace/record-table";
import { SelectField } from "@/components/workspace/select-field";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { useTableSort } from "@/components/workspace/use-table-sort";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";
import { WorkflowLabel } from "./workflow-label";

export function WorkflowSchedules({
  repositories,
}: {
  repositories: Array<{
    id: string;
    name: string;
    enabled: boolean;
    provider: string;
  }>;
}) {
  const queryClient = useQueryClient();
  const { params, update } = useUrlState();
  const workflowKey = params.get("workflow");
  const sort = useTableSort(["next"] as const, {
    defaultSort: { sort: "next", direction: "asc" },
  });
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<string | null>(null);

  const { toast } = useToast();
  const result = useQuery<WorkflowSchedule[]>({
    queryKey: ["workflow-schedules"],
    queryFn: async ({ signal }) => {
      return unwrap(apiClient().GET("/workflows/schedules", { signal }));
    },
  });
  /**
   * Temporal's schedule list is eventually consistent: a schedule that was just
   * written is missing from it for a second or so afterwards. Refetching once
   * would show the person a list without the change they just made — the row
   * they created simply absent — so this waits for the list to agree before
   * reporting success, and gives up rather than spinning forever.
   */
  async function refetchUntil(agrees: (rows: WorkflowSchedule[]) => boolean) {
    for (let attempt = 0; attempt < 8; attempt++) {
      const rows = await queryClient.fetchQuery<WorkflowSchedule[]>({
        queryKey: ["workflow-schedules"],
        queryFn: async ({ signal }) => unwrap(apiClient().GET("/workflows/schedules", { signal })),
        staleTime: 0,
      });
      if (agrees(rows)) return;
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  }

  const [removingSchedule, setRemovingSchedule] = useState<{
    sourceId: string;
    workflowKey: WorkflowCatalogEntry["key"];
    name: string;
  } | null>(null);
  async function save(
    sourceId: string,
    workflowKey: WorkflowCatalogEntry["key"],
    action: "save" | "enable" | "pause" | "delete",
    enabled: boolean,
    cron?: string,
  ) {
    if (workflowKey === "github.enrich_person") return;
    const key = `${sourceId}/${workflowKey}`;
    setPending(key);
    try {
      if (action === "delete") {
        await unwrap(
          apiClient().DELETE("/workflows/schedules/{sourceId}/{workflowKey}", {
            params: { path: { sourceId, workflowKey } },
          }),
        );
      } else {
        await unwrap(
          apiClient().PUT("/workflows/schedules", {
            body: { sourceId, workflowKey, enabled, cron },
          }),
        );
      }
      await refetchUntil((rows) => {
        const saved = rows.find(
          (row) => row.sourceId === sourceId && row.workflowKey === workflowKey,
        );
        return action === "delete"
          ? !saved
          : !!saved && saved.paused === !enabled && (cron === undefined || saved.cron === cron);
      });
      toast({
        variant: ToastVariant.SUCCESS,
        ...{
          save: {
            header: "Schedule saved",
            description: "The workflow runs on the new cadence.",
          },
          enable: {
            header: "Schedule enabled",
            description: "The workflow runs on its cadence from now on.",
          },
          pause: {
            header: "Schedule paused",
            description: "The workflow only runs when started manually.",
          },
          delete: {
            header: "Schedule deleted",
            description: "The workflow only runs when started manually.",
          },
        }[action],
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      return true;
    } catch (error) {
      if (action === "delete") throw error;
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not update schedule",
        description: (error as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
      return false;
    } finally {
      setPending(null);
    }
  }
  const labeledSources = repositories.map((source) => ({
    ...source,
    name: sourceLabel(source.provider, source.name),
  }));
  const rows = labeledSources
    .flatMap((source) =>
      workflows
        .filter(
          (workflow) =>
            isSchedulable(workflow.key) &&
            workflow.key.startsWith(`${source.provider}.`) &&
            (!workflowKey || workflow.key === workflowKey),
        )
        .map((workflow) => {
          const schedule = result.data?.find(
            (s) => s.sourceId === source.id && s.workflowKey === workflow.key,
          );
          const enabled = !!schedule && !schedule.paused;
          return { source, workflow, schedule, enabled };
        }),
    )
    .filter((row) =>
      `${row.source.name} ${row.workflow.name}`.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      const nextA = a.schedule?.nextRun;
      const nextB = b.schedule?.nextRun;
      if (!nextA) return nextB ? 1 : 0;
      if (!nextB) return -1;
      const difference = Date.parse(nextA) - Date.parse(nextB);
      return sort.query.direction === "desc" ? -difference : difference;
    });
  const currentSource = labeledSources.find((source) => source.id === params.get("scheduleSource"));
  const currentWorkflow = workflows.find(
    (workflow) =>
      workflow.key === params.get("scheduleWorkflow") &&
      isSchedulable(workflow.key) &&
      workflow.key.startsWith(`${currentSource?.provider}.`),
  );
  const editingSource = useRetainedValue(currentSource);
  const editingWorkflow = useRetainedValue(currentWorkflow);
  const editorOpen = !!currentSource && !!currentWorkflow;
  const editingSchedule = result.data?.find(
    (schedule) =>
      schedule.sourceId === editingSource?.id && schedule.workflowKey === editingWorkflow?.key,
  );
  const closeEditor = () => update({ scheduleSource: null, scheduleWorkflow: null });
  return (
    <>
      <ConfirmAction
        isOpen={!!removingSchedule}
        onOpenChange={(open) => {
          if (!open) setRemovingSchedule(null);
        }}
        header="Delete schedule?"
        description={`${removingSchedule?.name ?? "This workflow"} will only run when started manually.`}
        label="Delete schedule"
        isDestructive
        onConfirm={async () => {
          if (removingSchedule)
            await save(removingSchedule.sourceId, removingSchedule.workflowKey, "delete", false);
        }}
      />

      {editingSource &&
        editingWorkflow &&
        (result.isPending || result.isError ? (
          <SurfaceDialog open={editorOpen} title="Schedule" onClose={closeEditor}>
            <QueryState
              error={result.error}
              loadingMessage="Loading schedule…"
              onRetry={() => void result.refetch()}
            />
          </SurfaceDialog>
        ) : (
          <ScheduleForm
            open={editorOpen}
            key={`${editingSource.id}/${editingWorkflow.key}`}
            source={editingSource.name}
            workflow={editingWorkflow.name}
            schedule={editingSchedule}
            busy={!!pending}
            onClose={closeEditor}
            onSave={async (cron, enabled) => {
              if (await save(editingSource.id, editingWorkflow.key, "save", enabled, cron))
                closeEditor();
            }}
          />
        ))}
      <BaseToolbar
        leadingActions={
          <Input
            aria-label="Search schedules"
            placeholder="Search schedules…"
            value={search}
            onChange={(event) => setSearch(event)}
            type="search"
            size={InputSize.SMALL}
            label="Search schedules"
            hideLabel
          />
        }
      />
      <RecordTable
        label="Schedules"
        rowActions={(row) => row.actions}
        sort={sort}
        rowDetails={false}
        isLoading={result.isPending}
        error={result.error ?? undefined}
        onRetry={() => void result.refetch()}
        columns={[
          { key: "workflow", label: "Workflow", width: 280, minWidth: 280 },
          { key: "repository", label: "Source", width: "auto" },
          { key: "cron", label: "Cron (UTC)", width: 240 },
          { key: "next", label: "Next run", width: 200 },
          { key: "state", label: "State", width: 160 },
        ]}
        rows={rows.map(({ source, workflow, schedule, enabled }) => {
          const disabled = !!pending || !!result.error || result.isPending;
          return {
            workflow: <WorkflowLabel source={source.provider} name={workflow.name} />,
            repository: source.name,
            cron: schedule?.cron || "—",
            next: schedule ? date(schedule.nextRun ?? null, true) : undefined,
            state: (
              <Status
                value={schedule ? (enabled ? "active" : "paused") : "inactive"}
                label={schedule ? undefined : "Not scheduled"}
              />
            ),
            actions: (
              <>
                <MenuItem
                  label={schedule ? "Edit schedule" : "Create schedule"}
                  icon={schedule ? PencilSimpleIcon : PlusIcon}
                  isDisabled={disabled}
                  onSelect={() =>
                    update({ scheduleSource: source.id, scheduleWorkflow: workflow.key })
                  }
                />
                {schedule && (
                  <MenuItem
                    label={enabled ? "Pause schedule" : "Enable schedule"}
                    isDisabled={disabled}
                    onSelect={() =>
                      void save(source.id, workflow.key, enabled ? "pause" : "enable", !enabled)
                    }
                  />
                )}
                {schedule && (
                  <MenuItem
                    label="Delete schedule"
                    icon={TrashIcon}
                    variant={MenuItemVariant.ERROR}
                    isDisabled={disabled}
                    onSelect={() =>
                      setRemovingSchedule({
                        sourceId: source.id,
                        workflowKey: workflow.key,
                        name: `${workflow.name} on ${source.name}`,
                      })
                    }
                  />
                )}
              </>
            ),
          };
        })}
        rowKeys={rows.map(({ source, workflow }) => `${source.id}:${workflow.key}`)}
      />
    </>
  );
}

function ScheduleForm({
  open,
  source,
  workflow,
  schedule,
  busy,
  onClose,
  onSave,
}: {
  open: boolean;
  source: string;
  workflow: string;
  schedule?: WorkflowSchedule;
  busy: boolean;
  onClose: () => void;
  onSave: (cron: string, enabled: boolean) => Promise<void>;
}) {
  const initialCron = schedule?.cron ?? DEFAULT_SCHEDULE_CRON;
  const initialEnabled = schedule ? !schedule.paused : true;
  const [cron, setCron] = useState(initialCron);
  const [enabled, setEnabled] = useState(initialEnabled);
  const timezonePrefix = cron.match(/^(?:CRON_TZ|TZ)=(\S+)\s+/);
  const expression = timezonePrefix ? cron.slice(timezonePrefix[0].length) : cron;
  useDialogReset(open, () => {
    setCron(initialCron);
    setEnabled(initialEnabled);
  });
  return (
    <SurfaceDialog
      open={open}
      title={schedule ? "Edit schedule" : "Create schedule"}
      headerDescription={`${workflow} · ${source}`}
      onClose={onClose}
      busy={busy}
      dirty={cron !== initialCron || enabled !== initialEnabled}
      footer={
        <Button
          label={schedule ? "Save schedule" : "Create schedule"}
          isLoading={busy}
          isDisabled={busy || !expression.trim()}
          onClick={() => void onSave(cron, enabled)}
        />
      }
    >
      <CronInput
        label="Schedule"
        value={expression}
        placeholder="min hour dom mon dow"
        isDisabled={busy}
        fillWidth
        onChange={(value) => setCron(`${timezonePrefix?.[0] ?? ""}${value}`)}
      />
      <Field label="Timezone">
        <Input
          aria-label="Timezone"
          value={timezonePrefix?.[1] ?? ""}
          placeholder="UTC"
          disabled={busy}
          onChange={(value) =>
            setCron(value.trim() ? `CRON_TZ=${value.trim()} ${expression}` : expression)
          }
        />
      </Field>
      <Text>Defaults to UTC. Enter an IANA timezone such as America/New_York.</Text>
      <SelectField
        label="State"
        value={enabled ? "active" : "paused"}
        disabled={busy}
        options={[
          { value: "active", label: "Active" },
          { value: "paused", label: "Paused" },
        ]}
        onChange={(value) => setEnabled(value === "active")}
      />
    </SurfaceDialog>
  );
}
